import { Button } from '@mesa-labs/mesa-ui';
import React, { useState } from 'react';
import styled from 'styled-components';

const Editable = styled.div<{ $empty: boolean }>`
  border: 1px solid transparent;
  border-radius: 6px;
  color: ${(p) => (p.$empty ? '#98A2B3' : 'inherit')};
  cursor: text;
  margin: -4px -6px;
  padding: 4px 6px;

  &:hover { background: #F2F4F7; }
  &:focus-visible { border-color: #9E77ED; outline: none; }
`;

const EditInput = styled.input`
  border: 2px solid #9E77ED;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  padding: 4px 6px;
  width: 100%;
`;

const EditArea = styled.textarea`
  border: 2px solid #9E77ED;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  min-height: 90px;
  padding: 6px 8px;
  resize: vertical;
  width: 100%;
`;

const EditActions = styled.div`
  display: flex;
  gap: 6px;
  justify-content: flex-end;
  margin-top: 6px;
`;

type Props = {
  value: string;
  placeholder: string;
  multiline?: boolean;
  required?: boolean;
  readOnly?: boolean;
  display: (value: string) => React.ReactNode;
  onSave: (value: string) => void;
};

// Jira-style click-to-edit. Enter saves (Cmd/Ctrl+Enter when multiline), Escape cancels.
export default function InlineText({
  value, placeholder, multiline, required, readOnly, display, onSave,
}: Props): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const start = () => { setDraft(value); setEditing(true); };
  const canSave = !required || draft.trim().length > 0;
  const save = () => {
    if (!canSave) return;
    if (draft.trim() !== value) onSave(draft.trim());
    setEditing(false);
  };

  if (readOnly) {
    return <div>{value ? display(value) : <span style={{ color: '#98A2B3', fontSize: 13 }}>{placeholder}</span>}</div>;
  }

  if (!editing) {
    return (
      <Editable
        $empty={!value}
        role="button"
        tabIndex={0}
        title="Click to edit"
        onClick={start}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); start(); } }}
      >
        {value ? display(value) : <span style={{ fontSize: 13 }}>{placeholder}</span>}
      </Editable>
    );
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setEditing(false);
    if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
  };
  return (
    <div>
      {multiline
        ? <EditArea autoFocus value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown} />
        : <EditInput autoFocus value={draft} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown} />}
      <EditActions>
        <Button text="Cancel" color="secondary" fontSize={12} height={28} onClick={() => setEditing(false)} />
        <Button text="Save" fontSize={12} height={28} disabled={!canSave} onClick={save} />
      </EditActions>
    </div>
  );
}
